import type { FailablePost, PostMetrics } from '@mg/shared';
import {
  ApprovalState,
  ContentChannel,
  FetchFrequency,
  POST_METRICS_WINDOW_DAYS,
  IndustryPack,
  SourceHealth,
  SourceType,
  isPostOverdue,
} from '@mg/shared';
import type { BadgeTone } from '../components/ui/Badge';

const titleCase = (value: string): string =>
  value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

export const approvalStateLabel: Record<ApprovalState, string> = {
  [ApprovalState.DRAFT]: 'Draft',
  [ApprovalState.IN_REVIEW]: 'In review',
  [ApprovalState.REVIEWED]: 'Reviewed',
  [ApprovalState.COMPLIANCE_CHECK]: 'Compliance check',
  [ApprovalState.APPROVED]: 'Approved',
  [ApprovalState.SCHEDULED]: 'Scheduled',
  [ApprovalState.PUBLISHED]: 'Published',
  [ApprovalState.REJECTED]: 'Rejected',
  [ApprovalState.REVISE]: 'Revise',
};

export const approvalStateTone: Record<ApprovalState, BadgeTone> = {
  [ApprovalState.DRAFT]: 'neutral',
  [ApprovalState.IN_REVIEW]: 'amber',
  [ApprovalState.REVIEWED]: 'blue',
  [ApprovalState.COMPLIANCE_CHECK]: 'blue',
  [ApprovalState.APPROVED]: 'green',
  [ApprovalState.SCHEDULED]: 'brand',
  [ApprovalState.PUBLISHED]: 'green',
  [ApprovalState.REJECTED]: 'red',
  // "Changes requested" — reads as an attention/warning state like IN_REVIEW.
  [ApprovalState.REVISE]: 'amber',
};

export const sourceHealthTone: Record<SourceHealth, BadgeTone> = {
  [SourceHealth.HEALTHY]: 'green',
  [SourceHealth.DEGRADED]: 'amber',
  [SourceHealth.FAILING]: 'red',
  [SourceHealth.UNKNOWN]: 'neutral',
};

export const sourceTypeLabel: Record<SourceType, string> = {
  [SourceType.RSS]: 'RSS feed',
  [SourceType.APIFY]: 'Apify actor',
  [SourceType.REDDIT]: 'Reddit',
  [SourceType.NEWSLETTER]: 'Newsletter',
};

export const fetchFrequencyLabel: Record<FetchFrequency, string> = {
  [FetchFrequency.DAILY]: 'Daily',
  [FetchFrequency.WEEKLY]: 'Weekly',
  [FetchFrequency.BIWEEKLY]: 'Bi-weekly',
  [FetchFrequency.MONTHLY]: 'Monthly',
};

export const industryPackLabel: Record<IndustryPack, string> = {
  [IndustryPack.GENERAL_BUSINESS]: 'General Business',
  [IndustryPack.HEALTHCARE]: 'Healthcare',
  [IndustryPack.FINANCE]: 'Finance',
  [IndustryPack.LEGAL]: 'Legal',
};

export const channelLabel: Record<ContentChannel, string> = {
  [ContentChannel.BLOG]: 'Blog',
  [ContentChannel.SOCIAL]: 'Social',
  [ContentChannel.VIDEO]: 'Video',
  [ContentChannel.PODCAST]: 'Podcast',
};

export function enumLabel(value: string): string {
  return titleCase(value);
}

/** Format an ISO timestamp as a short, locale-aware date-time. */
/**
 * Long, unambiguous date+time — "July 31, 2026 at 5:00pm". Used where a date is being quoted back
 * to the user as a consequence of an action (e.g. "your post date of … will be removed"), which is
 * exactly where the compact `formatDateTime` ("Jul 31, 5:00 PM") is too terse: it omits the year
 * and reads like a label rather than a sentence.
 */
export function formatDateTimeLong(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  // "5:00 PM" → "5:00pm" so it sits inside prose without shouting.
  const time = d
    .toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    .replace(/\s*([AP])M/i, (_m, p: string) => `${p.toLowerCase()}m`);
  return `${date} at ${time}`;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Short, relative-ish label for a "last pulled" timestamp:
 * "Never", "Just now", "5m ago", "3h ago", "2d ago", or a short date past a week.
 */
export function formatRelative(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Never';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Format a YYYY-MM-DD (or ISO) string as a readable date. */
export function formatDate(value: string): string {
  const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Long-form date for headers, e.g. "Thursday, June 4, 2026". */
export function formatDateLong(value: string): string {
  const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

const compactMetric = (value: number, divisor: number, suffix: string): string => {
  // TRUNCATE to one decimal rather than round. Rounding pushes 999,999 up into "1000.0K" (or, if
  // the unit is picked after rounding, into "1M" — outside the K band entirely), and it also
  // overstates: a post with 1,999 views should not read "2K".
  const scaled = Math.trunc((value / divisor) * 10) / 10;
  // ".0" is false precision — "1.0K" should read "1K".
  return `${scaled.toFixed(1).replace(/\.0$/, '')}${suffix}`;
};

/**
 * Engagement counts, compacted: raw below 1,000, then "12.4K" and "1.2M".
 * Null is "the platform doesn't report this to us", never zero — so it renders an em dash, the
 * same "we have no value" mark the date helpers above use.
 */
/**
 * Are this post's metrics still worth waiting for?
 *
 * Only TRUE means shimmer. A post with no `metrics` object has no platform id and can never be
 * measured; one whose `fetchedAt` is set has a settled answer. The third case is the subtle one:
 * the sweep only refreshes posts published within POST_METRICS_WINDOW_DAYS, so an older post that
 * was never fetched will NEVER be fetched — shimmering it is a loading state that never resolves.
 */
export function metricsPending(post: {
  publishedAt: string | null;
  metrics?: PostMetrics | null;
}): boolean {
  if (post.publishedAt == null || post.metrics == null || post.metrics.fetchedAt != null) {
    return false;
  }
  const age = Date.now() - Date.parse(post.publishedAt);
  return Number.isFinite(age) && age <= POST_METRICS_WINDOW_DAYS * 24 * 60 * 60 * 1000;
}

export function formatMetric(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs < 1000) return String(value);
  // Bands exactly as specified: K covers 1,000–999,999, M starts at 1,000,000.
  return abs < 1_000_000 ? compactMetric(value, 1000, 'K') : compactMetric(value, 1_000_000, 'M');
}

/**
 * What a post/campaign chip can show. FAILED is NOT an ApprovalState — it's derived from the
 * release timestamps (see isPostFailed), so it lives alongside the enum rather than in it. That
 * keeps it out of the DB enum and the approval state machine, where it has no meaning.
 */
export type PostDisplayState = ApprovalState | 'FAILED' | 'SCHEDULING_ERROR';

export function displayStateLabel(state: PostDisplayState): string {
  if (state === 'FAILED') return 'Failed';
  if (state === 'SCHEDULING_ERROR') return 'Scheduling Error';
  return approvalStateLabel[state];
}

/**
 * Which of the two "this needs you" chips a post shows, or null when it shows neither.
 *
 * The split matters because the two ask for different things. FAILED means delivery was attempted
 * and the platform refused it — there is an error to read and something to retry. SCHEDULING_ERROR
 * means nothing was ever attempted: the time passed while the post waited, usually for an approval,
 * and the only fix is a new time. Calling that second case "Failed" sent people looking for a
 * platform error that does not exist.
 */
export function failureChipState(
  post: FailablePost,
  now: number = Date.now(),
): 'FAILED' | 'SCHEDULING_ERROR' | null {
  if (post.publishedAt) return null;
  if (post.publishError) return 'FAILED';
  return isPostOverdue(post, now) ? 'SCHEDULING_ERROR' : null;
}

export function displayStateTone(state: PostDisplayState): BadgeTone {
  if (state === 'FAILED') return 'red';
  // Amber, not red: nothing is broken — a date went by, and it needs a new one.
  if (state === 'SCHEDULING_ERROR') return 'amber';
  return approvalStateTone[state];
}
